import {
  DEFAULT_ATTRS,
  DEFAULT_MAX_LENGTH_BY_TYPE,
  FAKE_PREFIXES,
  LENGTH_LIMITS,
  MODE_APPOINTMENT,
  MODE_CONTACT,
  QUESTION_NAMES,
  QUESTION_TYPES,
  SUBTEXT_MAP,
  VALIDATION_MESSAGES,
} from './constants';
import { getGenderOptions, getStateProvinceOptionsGrouped } from './options';
import { validateFileUpload } from './validation-helpers';

export default class QuestionProcessor {
  store;
  startAt;
  numItems;
  formType;
  prescreener;
  questionRecord;
  excludeCategories;
  includeCategories;
  categoryCountMap;

  constructor(props) {
    let { startAt, formType, numItems, prescreener, includeCategories, excludeCategories } = props;

    this.formType = formType;
    this.categoryCountMap = {};
    this.startAt = startAt || 0;
    this.prescreener = prescreener;
    this.includeCategories = includeCategories || [];
    this.excludeCategories = excludeCategories || [];
    this.numItems = isNaN(numItems) ? Number.MAX_SAFE_INTEGER : numItems;
  }

  get questions() {
    return this.prescreener.questionRecord?.questions;
  }

  get categories() {
    return [...new Set(this.#visibleQuestions.map(q => q.category))];
  }

  get groupedQuestions() {
    let grouped = Object.groupBy(this.effectiveQuestions, q => q.category);

    return Object.entries(grouped).map(([category, questions]) => ({
      category,
      questions,
    }));
  }

  get effectiveQuestions() {
    return this.#visibleQuestions.map(question => {
      let type = this.#normalizeType(question);
      let options = this.#optionsFor(question, type);
      let validation = this.#validationFor(question, type);
      let subtext = this.#getSpecialCaseSubtext(question, validation);

      return {
        ...DEFAULT_ATTRS,
        ...question,
        ...validation,
        subtext,
        options,
        type,
      };
    });
  }

  get #visibleQuestions() {
    return this.questions?.filter(question => this.#shouldShowQuestion(question)) ?? [];
  }

  #shouldShowQuestion(question) {
    if (!this.#shouldShowCategory(question.category)) return false;

    let { showOnContactForm, showOnAppointmentForm } = question ?? {};
    let { formType } = this;

    const questionCanShow = Boolean(question.show);
    const canShowOnContact = showOnContactForm && formType === MODE_CONTACT && questionCanShow;
    const canShowOnAppointment =
      showOnAppointmentForm && formType === MODE_APPOINTMENT && questionCanShow;
    const canShow = canShowOnContact || canShowOnAppointment;

    const willShow = this.#applyContactFormSpecialCases(question, canShow);

    if (!willShow) return false;

    if (!this.#categoryBoundCheck(question.category)) return false;

    return willShow;
  }

  #categoryBoundCheck(category) {
    if (!this.startAt && this.numItems === Number.MAX_SAFE_INTEGER) return true;

    if (!this.categoryCountMap[category]) {
      this.categoryCountMap[category] = -1;
    }

    const categoryIndex = this.categoryCountMap[category] + 1;
    this.categoryCountMap[category] = categoryIndex;

    const categoryCount = categoryIndex + 1;

    return categoryIndex >= this.startAt && categoryCount <= this.numItems;
  }

  #shouldShowCategory(categoryName) {
    const hasIncludes = this.includeCategories?.length > 0;
    const hasExcludes = this.excludeCategories?.length > 0;

    if (hasIncludes && hasExcludes) return false;

    if (hasExcludes) {
      return !this.excludeCategories.includes(categoryName);
    }

    if (hasIncludes) {
      return this.includeCategories.includes(categoryName);
    }

    return true;
  }

  #normalizeType(question) {
    let { type, name } = question;

    switch (name) {
      case QUESTION_NAMES.GENDER:
      case QUESTION_NAMES.PREFERRED_CLINICIAN:
      case QUESTION_NAMES.STATE:
        return QUESTION_TYPES.SELECT;
      default:
        return type;
    }
  }

  #optionsFor(question, type) {
    let { name } = question;
    let opts = Array.isArray(question.options) ? question.options : [];
    let processedOptions;

    switch (name) {
      case QUESTION_NAMES.PREFERRED_CLINICIAN:
        processedOptions = this.#getClinicianOptions();
        break;
      case QUESTION_NAMES.GENDER:
        processedOptions = getGenderOptions();
        break;
      case QUESTION_NAMES.STATE:
        processedOptions = getStateProvinceOptionsGrouped();
        break;
      default:
        processedOptions = opts;
    }

    if (type === QUESTION_TYPES.SELECT) {
      return this.#processSelectOptions(processedOptions);
    }

    return processedOptions;
  }

  #processSelectOptions(options) {
    const normalizeOption = option => {
      return typeof option === 'string' ? { value: option, label: option } : option;
    };

    return options.map(opt => {
      if (opt.options) {
        return {
          ...opt,
          options: opt.options.map(normalizeOption),
        };
      }
      return normalizeOption(opt);
    });
  }

  #getClinicianOptions() {
    let isInPreview = this.prescreener.preview.isPreview;
    let previewFilter = clinician => {
      return isInPreview ? clinician.id.startsWith(FAKE_PREFIXES.CLINICIAN) : true;
    };
    let nameProcessor = clinician => {
      return clinician.fullName || [clinician.firstName, clinician.lastName].join(' ');
    };
    return this.prescreener.store.peekAll('clinician').filter(previewFilter).map(nameProcessor);
  }

  #validationFor(question, type) {
    const { name, required, hint, maxLength: questionMaxLength } = question;
    let requiredBoolean = this.#applyContactFormSpecialCases(question, required);

    let maxLength = Number(questionMaxLength);
    if (isNaN(maxLength) || !maxLength) {
      maxLength = DEFAULT_MAX_LENGTH_BY_TYPE[type] || LENGTH_LIMITS.TEXT_AREA_MAX;
    }

    const validationMessages = this.#getValidationMessages({ name, type, hint, maxLength });
    const customValidator = this.#getCustomValidator(question, type);

    return {
      required: requiredBoolean,
      maxLength,
      validationMessages,
      customValidator,
    };
  }

  #applyContactFormSpecialCases(question, currentValue) {
    const { name } = question;
    const { formType } = this;
    if (name === QUESTION_NAMES.STATE && formType === MODE_CONTACT) {
      return true;
    }
    return currentValue;
  }

  #getSpecialCaseSubtext(question, validation) {
    const { name, type } = question;
    let subtext = SUBTEXT_MAP[name] ?? '';
    if (!subtext && type === QUESTION_TYPES.TEXT) {
      subtext = validation.validationMessages.maxLength;
    }
    return subtext;
  }

  #getCustomValidator(question, type) {
    if (type === QUESTION_TYPES.FILE) {
      return (value, model) => {
        model.prescreener = this.prescreener;
        return validateFileUpload(value, model);
      };
    }
    return null;
  }

  #getValidationMessages(question) {
    const { name, type, hint, maxLength } = question;

    if (name === QUESTION_NAMES.STATE) {
      return {
        required: VALIDATION_MESSAGES.state,
      };
    }

    if (type === QUESTION_TYPES.MULTISELECT) {
      return {
        required: hint
          ? VALIDATION_MESSAGES.multiselect.hint
          : VALIDATION_MESSAGES.multiselect.default,
      };
    }

    return {
      required: VALIDATION_MESSAGES[type] || VALIDATION_MESSAGES.string,
      maxLength: `Limited to ${maxLength} characters`,
    };
  }
}
